import {
  NavLink,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./auth.js";
import { navigation } from "./navigation.js";
import { ClientDetailsScreen } from "./screens/client-details.js";
import { ClientsScreen } from "./screens/clients-screen.js";
import { LoginScreen } from "./screens/login-screen.js";
import { PlatformsScreen } from "./screens/platforms-screen.js";
import { TemplateEditorScreen } from "./screens/template-editor.js";
import { TemplatesScreen } from "./screens/templates-screen.js";
import { CreateScreen } from "./screens/create-screen.js";
import { GenerationsScreen } from "./screens/generations-screen.js";
import { GalleryScreen } from "./screens/gallery-screen.js";
import { SettingsScreen } from "./screens/settings-screen.js";

const titles = new Map<string, string>(
  navigation.map((item) => [item.path, item.label]),
);

export function App() {
  return (
    <AuthProvider>
      <AuthenticatedApp />
    </AuthProvider>
  );
}

function AuthenticatedApp() {
  const auth = useAuth();
  if (auth.status === "loading")
    return (
      <div className="app-loading">
        <span className="brand-mark">T</span>
        <p>Загружаем Image Studio…</p>
      </div>
    );
  if (auth.status === "anonymous") return <LoginScreen />;
  if (auth.status === "unavailable")
    return (
      <div className="app-loading" role="alert">
        <span className="brand-mark">T</span>
        <h1>Не удалось проверить вход</h1>
        <p>Проверьте подключение к интернету и повторите попытку.</p>
        <button className="primary" onClick={() => auth.retrySession()}>
          Повторить проверку
        </button>
      </div>
    );
  return <Shell />;
}

function Shell() {
  const location = useLocation();
  const title = location.pathname.startsWith("/clients/")
    ? "Карточка клиента"
    : location.pathname.startsWith("/templates/")
      ? "Редактор шаблона"
      : (titles.get(location.pathname) ?? "Image Studio");
  return (
    <div className="app-shell">
      <Sidebar />
      <main>
        <Topbar title={title} />
        <div className="workspace">
          <Routes>
            <Route path="/" element={<Navigate to="/clients" replace />} />
            <Route path="/clients" element={<ClientsScreen />} />
            <Route path="/templates" element={<TemplatesScreen />} />
            <Route
              path="/templates/:templateId"
              element={<TemplateEditorScreen />}
            />
            <Route path="/platforms" element={<PlatformsScreen />} />
            <Route
              path="/clients/:clientId"
              element={<ClientDetailsScreen />}
            />
            <Route path="/create" element={<CreateScreen />} />
            <Route path="/generations" element={<GenerationsScreen />} />
            <Route
              path="/generations/:batchId"
              element={<GenerationsScreen />}
            />
            <Route path="/gallery" element={<GalleryScreen />} />
            <Route path="/settings" element={<SettingsScreen />} />
            {navigation
              .filter(
                (item) =>
                  ![
                    "/create",
                    "/clients",
                    "/templates",
                    "/platforms",
                    "/generations",
                    "/gallery",
                    "/settings",
                  ].includes(item.path),
              )
              .map((item) => (
                <Route
                  key={item.path}
                  path={item.path}
                  element={<SkeletonScreen title={item.label} />}
                />
              ))}
            <Route path="*" element={<Navigate to="/clients" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

function Sidebar() {
  return (
    <aside className="sidebar">
      <div className="brand">
        <span className="brand-mark">T</span>
        <div>
          <strong>Tandem</strong>
          <small>Image Studio</small>
        </div>
      </div>
      <nav aria-label="Основная навигация">
        {navigation.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              isActive ? "nav-item active" : "nav-item"
            }
          >
            <span className="nav-icon">{item.icon}</span>
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="sidebar-help">
        <span>?</span> Помощь
      </div>
    </aside>
  );
}

function Topbar({ title }: { title: string }) {
  const auth = useAuth();
  return (
    <header className="topbar">
      <div>
        <span className="breadcrumbs">Image Studio /</span>
        <strong>{title}</strong>
      </div>
      <div className="top-actions">
        <div className="user-menu">
          <div className="avatar">
            {auth.user?.displayName.slice(0, 2).toUpperCase()}
          </div>
          <span>
            {auth.user?.displayName}
            <small>
              {auth.user?.role === "admin" ? "АДМИН" : "ПОЛЬЗОВАТЕЛЬ"}
            </small>
          </span>
          <button aria-label="Выйти" onClick={() => void auth.logout()}>
            ↪
          </button>
        </div>
      </div>
    </header>
  );
}

function SkeletonScreen({ title }: { title: string }) {
  const description =
    title === "Использование"
      ? "Здесь появятся сводные данные по объёму генераций и расходам."
      : "Здесь будут собраны общие настройки Image Studio.";
  return (
    <section className="panel skeleton-screen">
      <h1>{title}</h1>
      <p>{description}</p>
      <div className="skeleton-table" aria-hidden="true">
        <i />
        <i />
        <i />
        <i />
      </div>
    </section>
  );
}
